import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { api, extractError } from "@/lib/api";
import { PageHeader, Container } from "@/components/PageShell";
import { StatusBadge } from "@/components/Badges";
import { toast } from "sonner";

export default function Deployments() {
    const [items, setItems] = useState([]);
    const [active, setActive] = useState(null);
    const [detail, setDetail] = useState(null);
    const terminalRef = useRef(null);
    const location = useLocation();

    const load = async () => {
        try {
            const { data } = await api.get("/deployments");
            setItems(data);
            // pick first active or from ?id=
            const params = new URLSearchParams(location.search);
            const fromUrl = params.get("id");
            if (fromUrl) setActive(fromUrl);
            else if (!active && data.length > 0) setActive(data[0].id);
        } catch (e) { toast.error(extractError(e)); }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, []);

    // Poll active deployment every 1s if running
    useEffect(() => {
        if (!active) return;
        let cancel = false;
        const fetchOne = async () => {
            try {
                const { data } = await api.get(`/deployments/${active}`);
                if (cancel) return;
                setDetail(data);
                if (terminalRef.current) terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
                if (data.status === "running") setTimeout(fetchOne, 1200);
                else load();
            } catch (e) {
                if (!cancel) toast.error(extractError(e));
            }
        };
        fetchOne();
        return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active]);

    const classifyLine = (line) => {
        const l = line.toLowerCase();
        if (l.includes("[ok]") || l.includes("[done]") || l.includes("success")) return "log-success";
        if (l.includes("[error]") || l.includes("error") || l.includes("fail")) return "log-error";
        if (l.includes("[warn]") || l.includes("warning")) return "log-warn";
        if (l.includes("[info]") || l.startsWith("[")) return "log-info";
        return "";
    };

    return (
        <>
            <PageHeader
                title="Журнал деплоев"
                subtitle="Real-time вывод SSH-команд с серверов"
                testid="deployments-header"
            />
            <Container>
                <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4" data-testid="deployments-layout">
                    {/* List */}
                    <div className="bg-white border border-zinc-200 rounded-md overflow-hidden">
                        <div className="px-4 py-3 border-b border-zinc-200 text-xs uppercase tracking-wider text-zinc-500 font-medium">
                            Все деплои
                        </div>
                        {items.length === 0 ? (
                            <div className="p-6 text-center text-sm text-zinc-500">Пусто</div>
                        ) : (
                            <div className="divide-y divide-zinc-100 max-h-[70vh] overflow-auto">
                                {items.map((d) => (
                                    <button
                                        key={d.id}
                                        onClick={() => setActive(d.id)}
                                        data-testid={`deployment-item-${d.id}`}
                                        className={`w-full text-left p-3 hover:bg-zinc-50 transition-colors ${active === d.id ? "bg-zinc-50" : ""}`}
                                    >
                                        <div className="flex items-center gap-2">
                                            <StatusBadge status={d.status} />
                                        </div>
                                        <div className="text-xs font-mono mt-1 truncate">{d.action}</div>
                                        <div className="text-[10px] text-zinc-500 mt-0.5 font-mono">
                                            {new Date(d.started_at).toLocaleString("ru-RU")}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Terminal */}
                    <div className="bg-white border border-zinc-200 rounded-md overflow-hidden min-h-[400px] flex flex-col">
                        {!detail ? (
                            <div className="flex-1 flex items-center justify-center text-sm text-zinc-500">
                                Выберите деплой слева
                            </div>
                        ) : (
                            <>
                                <div className="px-5 py-3 border-b border-zinc-200 flex items-center justify-between">
                                    <div className="text-sm">
                                        <div className="font-mono font-medium">{detail.action}</div>
                                        <div className="text-[11px] text-zinc-500 mt-0.5 font-mono">
                                            id: {detail.id} · started: {new Date(detail.started_at).toLocaleString("ru-RU")}
                                        </div>
                                    </div>
                                    <StatusBadge status={detail.status} />
                                </div>
                                <div
                                    ref={terminalRef}
                                    className="terminal flex-1 p-4 overflow-auto min-h-[400px] max-h-[65vh]"
                                    data-testid="terminal-output"
                                >
                                    {(detail.logs || []).length === 0 ? (
                                        <div className="text-zinc-600">— waiting for output —</div>
                                    ) : (
                                        detail.logs.map((line, i) => (
                                            <div key={i} className={classifyLine(line)}>
                                                {line || "\u00A0"}
                                            </div>
                                        ))
                                    )}
                                    {detail.status === "running" && (
                                        <div className="log-info animate-pulse">_</div>
                                    )}
                                </div>
                                {detail.exit_code !== null && detail.exit_code !== undefined && (
                                    <div className="px-5 py-2 border-t border-zinc-200 text-xs font-mono text-zinc-500 bg-zinc-50">
                                        exit code: <span className={detail.exit_code === 0 ? "text-emerald-600" : "text-red-600"}>{detail.exit_code}</span>
                                        {detail.finished_at && ` · finished: ${new Date(detail.finished_at).toLocaleString("ru-RU")}`}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </Container>
        </>
    );
}
